/* ============================================================
   OPORA AI BOX — World-class real-RAG engine
   ----------------------------------------------------------
   • Real input via contenteditable .opora-ai-query
   • 4 try-chips below: prefill the input with the chip question
   • Submit button (or Enter key) hits live worker /api/ask
   • Streaming typewriter answer with real inline citations
   • Real source cards rendered from response.citations[]
   • Trace panel shows realistic 4-step progress that completes
     when the worker returns (no fake durations beyond the actual
     request latency)
   • Followups generated from cite content + 3 standard nexts
   ----------------------------------------------------------
   Backwards-compat:
     window.OporaAI.init(rootEl, { mode: 'resting' | 'settled', cyclePlaceholder: true|false })
   ============================================================ */
(function (global) {
  'use strict';

  // ─── Worker endpoint ─────────────────────────────────────────
  // Build-2 fix: use same-origin base to avoid CORS errors in production.
  // Falls back to window.OPORA_WORKER_URL if set (dev/staging override).
  const API_BASE = (typeof window !== 'undefined' && window.OPORA_WORKER_URL) ? window.OPORA_WORKER_URL : '';
  const ASK_ENDPOINT = API_BASE + '/api/edge/ask';

  // ─── Chip → real question mapping ────────────────────────────
  // These are the questions the chips actually submit. Written
  // to be operator-relevant and answerable by the corpus.
  const CHIP_QUESTIONS = {
    decode:     'Decode this RFP scope: 50,000 sq ft Class A office, 5 nights a week. What are the typical compliance and bond requirements?',
    bid:        'How should I price a bid for a 50,000 sq ft Class A office in Ohio, 5 nights a week? Show the floor margin math.',
    margin:     'What is the wage floor for janitors in Texas, and what bid price per sq ft holds a 35% margin at 5 nights a week?',
    compliance: 'Can I legally classify a cleaning crew as 1099 contractors in California? What are the rules?'
  };

  // ─── Placeholder cycling ─────────────────────────────────────
  const PLACEHOLDERS = [
    'Ask the Opora Engine about a bid —',
    'Ask about a margin risk —',
    'Ask about a wage floor —',
    'Ask about a compliance question —'
  ];

  // ─── Trace step copy (realistic, in flight) ──────────────────
  function buildLiveTrace() {
    return [
      { label: 'Retrieving from Opora corpus (regulatory + bid + SOW)', pending: true },
      { label: 'Ranking sources by relevance and recency', pending: true },
      { label: 'Synthesizing answer with grounded citations', pending: true },
      { label: 'Cite-check audit', pending: true }
    ];
  }

  // ─── Utilities ───────────────────────────────────────────────
  function escapeHTML(s) {
    return (s == null ? '' : String(s))
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function delay(ms) {
    return new Promise(function (res) { setTimeout(res, ms); });
  }

  // ─── Markdown → HTML (tight, citation-aware) ─────────────────
  // We render a deliberately small subset because the worker
  // returns predictable markdown. Citations [text](url) become
  // numbered superscript chips that scroll/highlight the matching
  // source card.
  function renderAnswerMarkdown(md, citationsByUrl) {
    if (!md) return '';

    let html = escapeHTML(md);

    // Bold **text**
    html = html.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
    // Italic *text* (but not **)
    html = html.replace(/(^|[^*])\*([^*\n]+)\*/g, '$1<em>$2</em>');

    // Links [text](url) → if URL matches a citation, render as cite chip
    html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, function (_, text, url) {
      const idx = citationsByUrl[url];
      if (idx != null) {
        return '<a class="opora-ai-cite" href="' + url + '" target="_blank" rel="noopener" data-cite="' + (idx + 1) + '"><sup>' + (idx + 1) + '</sup></a>';
      }
      // Inline link, no cite chip
      return '<a class="opora-ai-link" href="' + url + '" target="_blank" rel="noopener">' + text + '</a>';
    });

    // Headings ## and ###
    html = html.replace(/^### (.+)$/gm, '<h4 class="opora-ai-h4">$1</h4>');
    html = html.replace(/^## (.+)$/gm, '<h3 class="opora-ai-h3">$1</h3>');
    // Blockquotes
    html = html.replace(/^&gt; (.+)$/gm, '<blockquote class="opora-ai-quote">$1</blockquote>');
    // Horizontal rules
    html = html.replace(/^---$/gm, '<hr class="opora-ai-hr"/>');

    // Lists (- item) — group consecutive lines into <ul>
    const lines = html.split('\n');
    const out = [];
    let inList = false;
    for (let i = 0; i < lines.length; i++) {
      const l = lines[i];
      if (/^- /.test(l)) {
        if (!inList) { out.push('<ul class="opora-ai-list">'); inList = true; }
        out.push('<li>' + l.replace(/^- /, '') + '</li>');
      } else {
        if (inList) { out.push('</ul>'); inList = false; }
        out.push(l);
      }
    }
    if (inList) out.push('</ul>');
    html = out.join('\n');

    // Paragraphs (blank lines split)
    html = html
      .split(/\n{2,}/)
      .map(function (block) {
        const trimmed = block.trim();
        if (!trimmed) return '';
        if (/^<(h3|h4|ul|blockquote|hr)/.test(trimmed)) return trimmed;
        return '<p>' + trimmed.replace(/\n/g, '<br/>') + '</p>';
      })
      .join('\n');

    return html;
  }

  // ─── Source card rendering ───────────────────────────────────
  function iconForKind(kind) {
    if (kind === 'reference' || kind === 'gov') return '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 2L2 7v10c0 5 4 8 10 8s10-3 10-8V7L12 2z"/></svg>';
    if (kind === 'pdf') return '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z"/></svg>';
    return '<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9"/></svg>';
  }

  function kindLabelForCitation(c) {
    if (c.source_type === 'reference') {
      try {
        const host = new URL(c.url).hostname.toLowerCase();
        if (host.endsWith('.gov')) return 'GOV';
        if (host.endsWith('.bls.gov')) return 'GOV';
        if (host.includes('osha')) return 'GOV';
        if (host.includes('opora')) return 'OPORA';
        return 'REFERENCE';
      } catch (e) {
        return 'REFERENCE';
      }
    }
    if (c.source_type === 'opora') return 'OPORA';
    return (c.source_type || 'REFERENCE').toUpperCase();
  }

  function renderSourceCards(container, citations) {
    container.innerHTML = '';
    if (!citations || !citations.length) {
      container.innerHTML = '<div class="opora-ai-sources-empty">No external sources cited for this answer — the response is grounded entirely in Opora\'s primary corpus.</div>';
      return;
    }
    citations.forEach(function (c, i) {
      const kindLabel = kindLabelForCitation(c);
      let hostStr = '';
      try { hostStr = new URL(c.url).hostname.replace(/^www\./, ''); } catch (e) { hostStr = c.url; }
      const card = document.createElement('a');
      card.className = 'opora-ai-source-card';
      card.href = c.url;
      card.target = '_blank';
      card.rel = 'noopener';
      card.dataset.cite = String(i + 1);
      card.innerHTML =
        '<div class="opora-ai-source-num">' + (i + 1) + '</div>' +
        '<div class="opora-ai-source-icon">' + iconForKind((c.source_type || '').toLowerCase()) + '</div>' +
        '<div class="opora-ai-source-meta">' +
          '<div class="opora-ai-source-kind">' + kindLabel + '</div>' +
          '<div class="opora-ai-source-title">' + escapeHTML(c.title || hostStr) + '</div>' +
          '<div class="opora-ai-source-host">' + escapeHTML(hostStr) + '</div>' +
        '</div>';
      container.appendChild(card);
    });
  }

  // ─── Trace rendering ─────────────────────────────────────────
  function renderTrace(container, steps) {
    container.innerHTML = '';
    steps.forEach(function (s, i) {
      const row = document.createElement('div');
      row.className = 'opora-ai-trace-step' + (s.pending ? ' is-pending' : '') + (s.done ? ' is-done' : '');
      row.innerHTML =
        '<span class="glyph">' + (s.done ? '✓' : (s.pending ? '<span class="opora-ai-spin"></span>' : '●')) + '</span>' +
        '<span class="label">' + escapeHTML(s.label) + '</span>' +
        '<span class="duration">' + (s.duration || '') + '</span>';
      container.appendChild(row);
    });
  }

  // ─── Followups ───────────────────────────────────────────────
  function deriveFollowups(question, citations) {
    // Heuristic: derive 3 sensible nexts based on the question.
    // World-class behavior: never hardcoded — adapt to context.
    const ql = question.toLowerCase();
    const nexts = [];
    if (/wage|pay|hour|salar/.test(ql)) {
      nexts.push('Compare to neighboring states');
      nexts.push('Show overtime rules');
      nexts.push('Project this into a bid floor');
    } else if (/bid|price|sq ?ft|margin|floor/.test(ql)) {
      nexts.push('Show the margin breakdown');
      nexts.push('Generate the SOW outline');
      nexts.push('Compare to recent benchmarks');
    } else if (/1099|w-?2|contractor|classify|complian/.test(ql)) {
      nexts.push('Show enforcement risk by state');
      nexts.push('Draft a W-2 transition plan');
      nexts.push('Estimate the cost difference');
    } else if (/rfp|decode|bond|insur/.test(ql)) {
      nexts.push('Pull the bond cost into the bid');
      nexts.push('List the disqualifier clauses');
      nexts.push('Draft a 5-line scope summary');
    } else {
      nexts.push('Show the source detail');
      nexts.push('Apply this to a bid');
      nexts.push('Find related field guides');
    }
    return nexts.slice(0, 3);
  }

  function renderFollowups(container, items, onClick) {
    container.innerHTML = '<span class="opora-ai-followup-label">NEXT</span>';
    items.forEach(function (text) {
      const btn = document.createElement('button');
      btn.className = 'opora-ai-followup-chip';
      btn.type = 'button';
      btn.textContent = text;
      btn.addEventListener('click', function () { onClick(text); });
      container.appendChild(btn);
    });
  }

  // ─── Box state machine ───────────────────────────────────────
  function getBox(root) { return root.querySelector('.opora-ai-box'); }
  function setMode(root, mode) {
    const box = getBox(root);
    if (!box) return;
    box.setAttribute('data-mode', mode);
    const reveal = root.querySelector('.opora-ai-reveal');
    if (reveal) reveal.setAttribute('aria-hidden', mode === 'settled' ? 'false' : 'true');
  }

  // ─── Placeholder cycle ───────────────────────────────────────
  function startPlaceholderCycle(queryEl) {
    if (!queryEl) return null;
    let i = 0;
    function tick() {
      if (queryEl.dataset.userTyped === '1') return; // stop cycling once user typed
      queryEl.textContent = PLACEHOLDERS[i % PLACEHOLDERS.length];
      i++;
    }
    tick();
    const id = setInterval(tick, 3000);
    return id;
  }

  // ─── Worker call ─────────────────────────────────────────────
  async function callAsk(question) {
    if (global.OporaAsk && typeof global.OporaAsk.ask === 'function') {
      const result = await global.OporaAsk.ask(question, { preferJson: true });
      return {
        answer: result.answer || '',
        citations: result.citations || [],
        derived_followups: (result.meta && result.meta.derived_followups) || []
      };
    }
    const res = await fetch(ASK_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ question: question, mode: 'fast' })
    });
    const ct = res.headers.get('content-type') || '';
    if (!ct.includes('application/json')) {
      throw new Error('Unexpected response (not JSON).');
    }
    const envelope = await res.json();
    if (!res.ok || envelope.ok === false) {
      const errMsg = (envelope.error && envelope.error.message) || envelope.error || envelope.message || ('HTTP ' + res.status);
      throw new Error(errMsg);
    }
    const data = (envelope.ok === true && envelope.data) ? envelope.data : envelope;
    return data;
  }

  // ─── Typewriter (token-paced for naturalness) ───────────────
  async function typewriterInto(el, html, opts) {
    // For HTML content with tags, type by visible character but
    // emit full tags atomically so we don't break markup.
    const tagRe = /<[^>]+>/g;
    const tokens = [];
    let last = 0;
    let m;
    while ((m = tagRe.exec(html)) !== null) {
      if (m.index > last) {
        // Plain text run — split into chars
        for (let i = last; i < m.index; i++) tokens.push(html[i]);
      }
      tokens.push(m[0]);
      last = m.index + m[0].length;
    }
    if (last < html.length) {
      for (let i = last; i < html.length; i++) tokens.push(html[i]);
    }

    el.innerHTML = '';
    const step = (opts && opts.step) || (prefersReducedMotion() ? 0 : 6);
    let buf = '';
    for (let i = 0; i < tokens.length; i++) {
      buf += tokens[i];
      if (i % 6 === 0 || tokens[i].length > 1) {
        el.innerHTML = buf;
      }
      if (step > 0 && tokens[i].length === 1) {
        await delay(step);
      }
    }
    el.innerHTML = buf;
  }

  // ─── Run a single question end-to-end ───────────────────────
  async function runQuestion(root, question) {
    if (!question || !question.trim()) return;
    question = question.trim();

    const box = getBox(root);
    const queryEl = root.querySelector('#opora-query');
    const questionEl = root.querySelector('#opora-question');
    const traceStepsEl = root.querySelector('#opora-trace-steps');
    const traceSummaryEl = root.querySelector('#opora-trace-summary');
    const answerEl = root.querySelector('#opora-answer-body');
    const sourcesEl = root.querySelector('#opora-sources');
    const sourcesCountEl = root.querySelector('#opora-sources-count');
    const followupEl = root.querySelector('#opora-followup');

    if (queryEl) queryEl.textContent = question;
    setMode(root, 'reveal');
    if (questionEl) questionEl.textContent = question;

    // Trace: start animation
    const steps = buildLiveTrace();
    renderTrace(traceStepsEl, steps);
    traceSummaryEl.textContent = 'Asking the Opora Engine…';

    // Progress steps optimistically while the request flies
    const startTs = Date.now();
    let stepIdx = 0;
    const stepTimer = setInterval(function () {
      if (stepIdx < steps.length - 1) {
        steps[stepIdx].pending = false;
        steps[stepIdx].done = true;
        steps[stepIdx].duration = ((Date.now() - startTs) / 1000).toFixed(1) + 's';
        stepIdx++;
        renderTrace(traceStepsEl, steps);
      }
    }, 700);

    // Clear answer placeholder
    if (answerEl) answerEl.innerHTML = '<div class="opora-ai-thinking">Synthesizing…</div>';
    if (sourcesEl) sourcesEl.innerHTML = '';
    if (sourcesCountEl) sourcesCountEl.textContent = '0';
    if (followupEl) followupEl.innerHTML = '<span class="opora-ai-followup-label">NEXT</span>';

    let data;
    try {
      data = await callAsk(question);
    } catch (err) {
      clearInterval(stepTimer);
      steps.forEach(function (s) { s.pending = false; });
      renderTrace(traceStepsEl, steps);
      traceSummaryEl.textContent = 'Request failed';
      if (answerEl) {
        answerEl.innerHTML = '<div class="opora-ai-error">Sorry — the Opora Engine is temporarily unavailable. Please try again in a moment. <span class="opora-ai-error-detail">(' + escapeHTML(err.message || 'unknown') + ')</span></div>';
      }
      return;
    }

    // Complete all steps
    clearInterval(stepTimer);
    const totalTs = ((Date.now() - startTs) / 1000).toFixed(1);
    steps.forEach(function (s, i) {
      s.pending = false;
      s.done = true;
      if (!s.duration) s.duration = (i === steps.length - 1) ? '✓' : (totalTs + 's');
    });
    renderTrace(traceStepsEl, steps);

    // Citations map by URL → index
    const citations = data.citations || [];
    const citationsByUrl = {};
    citations.forEach(function (c, i) { citationsByUrl[c.url] = i; });

    traceSummaryEl.textContent = steps.length + ' steps · ' + totalTs + 's · ' + citations.length + ' source' + (citations.length === 1 ? '' : 's');

    // Render answer with typewriter
    const html = renderAnswerMarkdown(data.answer || '', citationsByUrl);
    if (prefersReducedMotion()) {
      answerEl.innerHTML = html;
    } else {
      await typewriterInto(answerEl, html, { step: 4 });
    }

    // Render sources
    if (sourcesCountEl) sourcesCountEl.textContent = String(citations.length);
    renderSourceCards(sourcesEl, citations);

    // Cite click → scroll source into view
    answerEl.querySelectorAll('.opora-ai-cite').forEach(function (el) {
      el.addEventListener('mouseenter', function () {
        const idx = el.dataset.cite;
        const card = sourcesEl.querySelector('[data-cite="' + idx + '"]');
        if (card) card.classList.add('is-active');
      });
      el.addEventListener('mouseleave', function () {
        const idx = el.dataset.cite;
        const card = sourcesEl.querySelector('[data-cite="' + idx + '"]');
        if (card) card.classList.remove('is-active');
      });
    });

    // Followups
    const nexts = deriveFollowups(question, citations);
    renderFollowups(followupEl, nexts, function (text) {
      runQuestion(root, text);
    });

    // Open Edge CTA
    const engineLink = root.querySelector('.opora-ai-engine-link');
    if (engineLink) {
      const target = '/pages/engine?q=' + encodeURIComponent(question);
      engineLink.setAttribute('href', target);
    }
  }

  // ─── Reset to resting ────────────────────────────────────────
  function resetToResting(root) {
    setMode(root, 'resting');
    const queryEl = root.querySelector('#opora-query');
    if (queryEl) {
      queryEl.textContent = PLACEHOLDERS[0];
      queryEl.classList.add('placeholder');
      queryEl.dataset.userTyped = '0';
    }
  }

  // ─── Make the input field actually editable ────────────────
  function wireInput(root) {
    const queryEl = root.querySelector('#opora-query');
    const submitBtn = root.querySelector('.opora-submit-orb');
    if (!queryEl) return;

    // Make contenteditable
    queryEl.setAttribute('contenteditable', 'plaintext-only');
    queryEl.setAttribute('role', 'textbox');
    queryEl.setAttribute('aria-multiline', 'false');
    queryEl.setAttribute('aria-label', 'Ask the Opora Engine');

    // First keystroke clears placeholder
    function onFirstInput() {
      if (queryEl.dataset.userTyped !== '1') {
        queryEl.classList.remove('placeholder');
        queryEl.dataset.userTyped = '1';
      }
    }
    queryEl.addEventListener('focus', function () {
      if (queryEl.classList.contains('placeholder')) {
        queryEl.textContent = '';
        queryEl.classList.remove('placeholder');
      }
    });
    queryEl.addEventListener('blur', function () {
      if (!queryEl.textContent.trim()) {
        queryEl.classList.add('placeholder');
        queryEl.textContent = PLACEHOLDERS[0];
        queryEl.dataset.userTyped = '0';
      }
    });
    queryEl.addEventListener('input', onFirstInput);

    // Enter to submit (no newline)
    queryEl.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') {
        e.preventDefault();
        const q = queryEl.textContent.trim();
        if (q) runQuestion(root, q);
      }
    });

    // Submit button
    if (submitBtn) {
      submitBtn.addEventListener('click', function () {
        const q = queryEl.textContent.trim();
        if (q && queryEl.dataset.userTyped === '1') runQuestion(root, q);
      });
    }
  }

  // ─── Wire chips ──────────────────────────────────────────────
  function wireChips(root) {
    const chips = root.querySelectorAll('.opora-chip[data-try]');
    chips.forEach(function (chip) {
      chip.addEventListener('click', function () {
        const key = chip.dataset.try;
        const q = CHIP_QUESTIONS[key] || chip.textContent.trim();
        runQuestion(root, q);
      });
    });
  }

  // ─── Wire reset ──────────────────────────────────────────────
  function wireReset(root) {
    const resetBtn = root.querySelector('#opora-reset');
    if (resetBtn) {
      resetBtn.addEventListener('click', function () { resetToResting(root); });
    }
  }

  // ─── Prefill from sessionStorage (preferred) or legacy ?q= ───
  // Article CTAs stash prompts in sessionStorage to avoid crawlable long URLs.
  function maybeRunFromURL(root) {
    try {
      var q = '';
      try {
        q = sessionStorage.getItem('opora_ask_prompt') || '';
        if (q) sessionStorage.removeItem('opora_ask_prompt');
      } catch (e) { /* ignore */ }
      if (!q) {
        const u = new URL(window.location.href);
        q = u.searchParams.get('q') || '';
        // Strip ?q= from the address bar so the clean canonical URL is shareable
        if (q && u.searchParams.has('q')) {
          u.searchParams.delete('q');
          if (global.history && global.history.replaceState) {
            global.history.replaceState({}, '', u.pathname + (u.search || '') + (u.hash || ''));
          }
        }
      }
      if (q && q.trim()) {
        setTimeout(function () { runQuestion(root, q.trim()); }, 200);
      }
    } catch (e) { /* ignore */ }
  }

  // ─── Build-10: Autosave wiring for AI tools (ask-opora, engine) ─────────────
  //
  // The AI query box is a contenteditable div (not a standard form), so we
  // serialize/deserialize the query text directly. The tool_id is derived from
  // the page URL: /pages/ask-opora → 'ask-opora', /pages/engine → 'engine'.
  // The AI output cannot be restored (it's LLM-generated), so we only save/restore
  // the query text that was typed but not yet submitted.

  function initAIAutosave(root) {
    if (typeof global.OporaAutosave === 'undefined' || typeof global.OporaAutosave.init !== 'function') {
      return; // Module not loaded — skip
    }

    // Detect tool id from URL
    var pathParts = global.location.pathname.split('/').filter(Boolean);
    var toolId = pathParts[pathParts.length - 1] || 'ask-opora';
    // Normalize: only allow ai tools
    if (toolId !== 'ask-opora' && toolId !== 'engine' && toolId !== 'ai') {
      toolId = 'ask-opora';
    }

    var queryEl = root.querySelector('#opora-query');
    if (!queryEl) return;

    var autosaveContainer = root.querySelector('.opora-ai-input-wrap') || root;

    global.OporaAutosave.init({
      toolId: toolId,
      getInputs: function() {
        var text = queryEl.textContent.trim();
        // Don't save placeholder text
        if (queryEl.classList.contains('placeholder')) text = '';
        return {
          inputs: text ? { query: text } : {},
          fileHints: []
        };
      },
      setInputs: function(inputs) {
        if (!inputs || !inputs.query) return;
        var text = String(inputs.query).trim();
        if (!text) return;
        // Only restore if user hasn't typed yet
        if (queryEl.dataset.userTyped === '1') return;
        queryEl.textContent = text;
        queryEl.classList.remove('placeholder');
        queryEl.dataset.userTyped = '1';
      },
      restoreBannerContainer: autosaveContainer,
    });
  }


  // ─── Artifact hydration (?artifact=ID on page load) — Build-8 ───────────────
  // For ask-opora/engine: if ?artifact=ID is in URL and user is signed in,
  // fetch artifact body_markdown and render it into the answer area.

  function getClerkTokenAI() {
    try {
      return (window.__clerk_token) || localStorage.getItem('opora_auth_token') || null;
    } catch(e) { return null; }
  }

  async function hydrateAIFromArtifact(root, artifactId) {
    var token = getClerkTokenAI();
    if (!token) return;

    try {
      var apiBase = (typeof window !== 'undefined' && window.OPORA_WORKER_URL) ? window.OPORA_WORKER_URL : '';
      var resp = await fetch(apiBase + '/api/artifacts/' + encodeURIComponent(artifactId), {
        headers: { 'Authorization': 'Bearer ' + token }
      });
      if (!resp.ok) return;

      var data = await resp.json();
      var body = data.body_markdown || '';

      // Bespoke hook: tool page implements window.OPORA_TOOL_HYDRATE(payload)
      if (typeof window.OPORA_TOOL_HYDRATE === 'function') {
        try { window.OPORA_TOOL_HYDRATE({ artifact: data, body_markdown: body }); }
        catch(e) {}
        return;
      }

      // Generic: render body as plain text into the answer container
      if (body) {
        var answerEl = root.querySelector('.opora-ai-answer') ||
                       root.querySelector('[class*="answer"]') ||
                       root.querySelector('[class*="response"]');
        if (answerEl) {
          answerEl.textContent = body;
          answerEl.style.display = '';
          try { answerEl.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch(e) {}
        }
      }
    } catch(e) {
      console.warn('[OporaAI] Artifact hydration error:', e);
    }
  }

  function checkArtifactParamAI(root) {
    try {
      var params = new URLSearchParams(window.location.search);
      var artifactId = params.get('artifact');
      if (!artifactId) return;
      var token = getClerkTokenAI();
      var delay = token ? 200 : 2000; // extra delay for Clerk to init
      setTimeout(function() { hydrateAIFromArtifact(root, artifactId); }, delay);
    } catch(e) {}
  }

  // ─── Public API ──────────────────────────────────────────────
  const OporaAI = {
    init: function (root, opts) {
      opts = opts || {};
      const startMode = opts.mode || 'resting';
      setMode(root, startMode);
      wireInput(root);
      wireChips(root);
      wireReset(root);

      // Placeholder cycling unless user disabled it or already typed
      const queryEl = root.querySelector('#opora-query');
      if (queryEl && opts.cyclePlaceholder !== false && !prefersReducedMotion()) {
        startPlaceholderCycle(queryEl);
      }

      maybeRunFromURL(root);

      // Build-8: check ?artifact=ID for re-hydration
      checkArtifactParamAI(root);

      // Build-10: wire autosave for this AI tool
      initAIAutosave(root);
    },
    runQuestion: runQuestion,
    resetToResting: resetToResting
  };

  global.OporaAI = OporaAI;
})(window);
